import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { FaAppleAlt, FaCheck, FaLock } from 'react-icons/fa';
import { END_POINT } from '../../../Redux/AdminReducer/action';
import { DIET_SECTIONS, emptyDietForm } from '../../../Files/dietForm';

const createItem = async (data, navigate, setIsPorcessing, threapyType) => {
  try {
    // Filled in by patients without a login, so the clinic is resolved from the host.
    await axios.post(`${END_POINT}/alternative_therapy/public/create/${String(threapyType).toLowerCase()}/`, data, {
      headers: { 'X-Frontend-Host': window.location.host },
    });
    setIsPorcessing(false);
    const isPublic = window.location.pathname.startsWith('/public');
    navigate(isPublic ? `/public/afterAction` : `/web/afterAction`);
    toast.success('Form submitted successfully');
  } catch (error) {
    toast.error(error?.response?.data || 'Something went wrong');
    setIsPorcessing(false);
  }
};

const labelClass = 'block text-sm font-medium leading-snug text-slate-700';
const controlClass =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20';

const Field = ({ field, value, onChange }) => {
  const shared = {
    id: field.name,
    name: field.name,
    value: value || '',
    onChange,
    className: controlClass,
  };

  return (
    <div className={field.full ? 'sm:col-span-2' : ''}>
      <label htmlFor={field.name} className={labelClass}>
        {field.label}
        {field.unit && <span className="ml-1 font-normal text-slate-400">({field.unit})</span>}
      </label>

      <div className="mt-1.5">
        {field.type === 'select' ? (
          <select {...shared} className={`${controlClass} appearance-none bg-[length:1rem] bg-[right_0.85rem_center] bg-no-repeat pr-10`} style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E\")" }}>
            <option value="">Select an option</option>
            {field.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : field.type === 'textarea' ? (
          <textarea {...shared} rows={3} placeholder={field.placeholder || ''} className={`${controlClass} resize-y`} />
        ) : (
          <input {...shared} type={field.type} placeholder={field.placeholder || ''} min={field.min} max={field.max} />
        )}
      </div>
    </div>
  );
};

export default function Nutrion({ therapy }) {
  const params = useParams();
  // The diet link from a prescription has no therapy segment, only the patient.
  const threapyType = therapy || params.threapyType;
  const { patientId } = params;
  const [formData, setFormData] = useState(emptyDietForm);
  const navigate = useNavigate();
  const [isProcessing, setIsPorcessing] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // A gentle nudge rather than validation — every question is optional, but the
  // patient should be able to see they have missed a whole section.
  const answered = useMemo(() => Object.values(formData).filter((v) => String(v || '').trim() !== '').length, [formData]);
  const total = useMemo(() => Object.keys(formData).length, [formData]);
  const percent = total ? Math.round((answered / total) * 100) : 0;

  const handleForm = (e) => {
    e.preventDefault();

    const trimmedFormData = Object.entries(formData).reduce((acc, [key, value]) => {
      acc[key] = typeof value === 'string' ? value.trim() : value;
      return acc;
    }, {});

    if (!answered) {
      toast.error('Please answer at least a few questions before submitting');
      return;
    }

    setIsPorcessing(true);
    createItem(patientId ? { ...trimmedFormData, patientId } : trimmedFormData, navigate, setIsPorcessing, threapyType);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
        <header className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <FaAppleAlt className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">Diet &amp; Nutrition Questionnaire</h1>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                Your answers help your doctor build a diet plan around how you actually eat and live. There are no right
                answers — please answer as honestly as you can. Nothing here is compulsory.
              </p>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between text-xs font-medium text-slate-500">
              <span>
                {answered} of {total} answered
              </span>
              <span>{percent}%</span>
            </div>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-emerald-500 transition-all duration-300" style={{ width: `${percent}%` }} />
            </div>
          </div>

          <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-400">
            <FaLock className="h-3 w-3" />
            Shared only with your treating doctor.
          </p>
        </header>

        <form onSubmit={handleForm} className="mt-6 space-y-6">
          {DIET_SECTIONS.map((section, index) => (
            <section key={section.id} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="flex items-baseline gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-white">
                  {index + 1}
                </span>
                <div>
                  <h2 className="text-base font-bold text-slate-900 sm:text-lg">{section.title}</h2>
                  <p className="mt-0.5 text-sm text-slate-500">{section.description}</p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
                {section.fields.map((field) => (
                  <Field key={field.name} field={field} value={formData[field.name]} onChange={handleInputChange} />
                ))}
              </div>
            </section>
          ))}

          <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/90 px-4 py-4 backdrop-blur sm:mx-0 sm:rounded-2xl sm:border sm:px-6 sm:shadow-sm">
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
              <p className="text-xs text-slate-500">You can submit even if some questions are left blank.</p>
              <button
                type="submit"
                disabled={isProcessing}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-emerald-600/20 transition hover:bg-emerald-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {isProcessing ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Submitting…
                  </>
                ) : (
                  <>
                    <FaCheck className="h-3.5 w-3.5" />
                    Submit Questionnaire
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
